import React from "react";
import T from '../../components/common/T';
import { useProcurement } from "../../context/ProcurementContext";
import { toast } from "react-toastify";
import TableActionButtons from "../../components/common/TableActionButtons";

// === SVG Icons ===
const EditIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232a3 3 0 014.243 4.243L9 19H5v-4l10.232-9.768z" />
  </svg>
);

const DeleteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  }).format(amount || 0);

const ProcurementListView = ({ data = [], onEdit = () => {} }) => {
  const { deleteProcurement } = useProcurement() || {};

  const handleEdit = (id) => {
    onEdit(id);
    toast.info("Opening edit form...", {
      position: "top-right",
      autoClose: 1200,
      theme: "colored",
    });
  };

  const handleDelete = (id) => {
    if (typeof deleteProcurement === "function") {
      deleteProcurement(id);
      toast.success("Record deleted successfully!", {
        position: "top-right",
        autoClose: 2000,
        theme: "colored",
      });
    } else {
      toast.error("Delete failed: deleteProcurement not available", {
        position: "top-right",
        autoClose: 2000,
        theme: "colored",
      });
    }
  };

  return (
    <div className="overflow-x-auto bg-white rounded-lg shadow">
      <table className="w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"><T>PO Number</T></th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"><T>Project</T></th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"><T>Vendor</T></th>

            {/* NEW COLUMNS */}
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"><T>Material</T></th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"><T>Qty</T></th>

            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"><T>Budget</T></th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"><T>Status</T></th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase"><T>Actions</T></th>
          </tr>
        </thead>

        <tbody className="bg-white divide-y divide-gray-200">
          {data.length > 0 ? (
            data.map((p) => (
              <tr key={p.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">{p.poNumber}</td>
                <td className="px-6 py-4">{p.projectName}</td>
                <td className="px-6 py-4">{p.vendorName}</td>

                {/* NEW MATERIAL WITH ID */}
                <td className="px-6 py-4">
                  <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800">
                    {p.materialName || "—"}
                  </span>
                  <div className="text-[10px] text-gray-400">ID: {p.materialId || "N/A"}</div>
                </td>

                {/* NEW QTY */}
                <td className="px-6 py-4 font-semibold text-gray-700">
                  {p.quantity || 0}
                </td>

                <td className="px-6 py-4">{formatCurrency(p.budgetAllocated)}</td>

                <td className="px-6 py-4">
                  <span
                    className={`px-3 py-1 text-xs rounded-full ${
                      p.status === "Approved"
                        ? "bg-green-100 text-green-800"
                        : p.status === "Sent"
                        ? "bg-yellow-100 text-yellow-800"
                        : p.status === "Fulfilled"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {p.status}
                  </span>
                </td>

                <td className="px-6 py-4 text-right">
                  <TableActionButtons
                    onEdit={() => handleEdit(p.id)}
                    onDelete={() => handleDelete(p.id)}
                    extraActions={
                      <button
                        onClick={() => window.location.href = `/inventory/${p.materialId}`}
                        className="text-indigo-600 hover:text-indigo-800 text-xs font-semibold underline mr-1 cursor-pointer"
                        title="View Stock"
                      >
                        View Stock
                      </button>
                    }
                  />
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="8" className="text-center py-6 text-gray-500">
                No procurement records found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ProcurementListView;
